import { memo, useEffect, useRef, useState } from "react";
import {
	AlertTriangle, ArrowRight, Check, CheckCircle2, Copy,
	ExternalLink, Info, Loader2, Play, Sparkles, Wrench, XCircle, Zap
} from "lucide-react";
import { Badge } from "@/components/atoms/studio/ui/badge";
import { Button } from "@/components/atoms/studio/ui/button";
import { SkillResultMarkdown } from "@/components/organisms/studio/SkillResultMarkdown";
import type {
	ExecuteResult,
	ExecutionState,
	FlakeBudget,
	HealStage,
	OutcomeMessage,
} from "@/services/testing";
import { openRunReport } from "@/services/testing";
import { useRunSuggestions } from "@/hooks/testing/useRunSuggestions";
import { formatTokens } from "@/utils/testing/planDisplay";
import { AnimatedCheck, AnimatedX } from "./ExecutionFeed";
import { healStageLabel } from "./HealProposalCard";
import { TRIAGE_META } from "./triageMeta";

// ─── Run result summary ───────────────────────────────────────────────────────

/** How long the copied-tick stays before reverting. */
const COPY_FEEDBACK_MS = 1500;

export function CopyButton({ getText, title = "Copy" }: { getText: () => string; title?: string }) {
	const [copied, setCopied] = useState(false);
	// Cleared on unmount — switching session inside the feedback window used to setState on an
	// unmounted component.
	const resetTimer = useRef<ReturnType<typeof setTimeout>>();
	useEffect(() => () => clearTimeout(resetTimer.current), []);
	function doCopy() {
		const text = getText();
		const apply = () => {
			setCopied(true);
			clearTimeout(resetTimer.current);
			resetTimer.current = setTimeout(() => setCopied(false), COPY_FEEDBACK_MS);
		};
		if (navigator?.clipboard?.writeText) {
			navigator.clipboard.writeText(text).then(apply).catch(() => {
				const el = document.createElement("textarea");
				el.value = text;
				document.body.appendChild(el);
				el.select();
				document.execCommand("copy");
				document.body.removeChild(el);
				apply();
			});
		}
	}
	return (
		<button
			type="button"
			onClick={doCopy}
			title={title}
			aria-label={title}
			className="inline-flex h-6 w-6 items-center justify-center rounded border border-border/60 bg-card/60 text-muted-foreground/70 transition-colors hover:bg-muted/40 hover:text-foreground"
		>
			{copied ? <Check className="h-3 w-3 text-success-strong" /> : <Copy className="h-3 w-3" />}
		</button>
	);
}

/**
 * "report" pill. Report assets are fetched by the BROWSER, which cannot attach the bearer +
 * identity headers `/proxy/testing` requires — so a hand-built href off `summary.report_url` 401s.
 * Every report open mints a short-lived signed URL first (`openRunReport`), and the report index
 * links onward to the trace, screenshots and video, so this one entry point covers all artifacts.
 */
export function RunReportPill({
	runId,
	label = "report",
	className,
}: {
	runId: string;
	label?: string;
	className?: string;
}) {
	const [opening, setOpening] = useState(false);
	const [error, setError] = useState<string | null>(null);

	async function open() {
		setOpening(true);
		setError(null);
		try {
			const url = await openRunReport(runId);
			if (!url) setError("Report link could not be opened.");
		} catch (e) {
			setError((e as Error).message || "Report is not available for this run.");
		} finally {
			setOpening(false);
		}
	}

	return (
		<>
			<button
				type="button"
				onClick={() => void open()}
				disabled={opening}
				title="Open the full Playwright report (screenshots, trace, video)"
				className={
					className ??
					"inline-flex min-h-[24px] items-center gap-1 rounded border border-border bg-card/60 px-1.5 py-0.5 font-mono text-[11px] text-foreground/80 hover:bg-card disabled:opacity-60"
				}
			>
				{opening ? (
					<Loader2 className="h-3 w-3 animate-spin" />
				) : (
					<ExternalLink className="h-3 w-3" />
				)}
				{label}
			</button>
			{error && (
				<span className="text-[11px] text-destructive-strong" role="alert">
					{error}
				</span>
			)}
		</>
	);
}

/**
 * The amber truth behind a green verdict. `status` is only ever passed|failed, so a run that only
 * went green after a timing retry — or that passed but left test data behind — is invisible unless
 * `execution_state` is read. Both are PASSES; neither is clean.
 */
function ExecutionStateChip({ state }: { state: ExecutionState }) {
	if (state === "passed_with_retry") {
		return (
			<span
				className="inline-flex items-center gap-1 rounded border border-warning/40 bg-warning/10 px-1.5 py-0.5 text-[11px] font-medium text-warning-strong"
				title="This run only went green after a retry — treat it as a flake signal, not a clean pass."
			>
				<AlertTriangle className="h-3 w-3" />
				passed after retry
			</span>
		);
	}
	if (state === "degraded") {
		return (
			<span
				className="inline-flex items-center gap-1 rounded border border-warning/40 bg-warning/10 px-1.5 py-0.5 text-[11px] font-medium text-warning-strong"
				title="The test passed, but teardown did not complete — some test data may still be in your org."
			>
				<AlertTriangle className="h-3 w-3" />
				passed, cleanup warnings
			</span>
		);
	}
	return null;
}

/** How much recovery machinery the verdict leaned on. Only meaningful on a retried pass. */
function FlakeBudgetCaption({ budget }: { budget: FlakeBudget }) {
	const parts: string[] = [];
	if (budget.timing_retries > 0) parts.push(`${budget.timing_retries} timing retr${budget.timing_retries === 1 ? "y" : "ies"}`);
	if (budget.ai_recoveries > 0) parts.push(`${budget.ai_recoveries} AI recover${budget.ai_recoveries === 1 ? "y" : "ies"}`);
	if (budget.user_clarifications > 0) parts.push(`${budget.user_clarifications} clarification${budget.user_clarifications === 1 ? "" : "s"}`);
	if (parts.length === 0) return null;
	return (
		<div className="text-[11px] text-warning-strong/80">
			Reached its verdict with {parts.join(", ")}.
		</div>
	);
}

// ─── Run mascot (right panel) ─────────────────────────────────────────────────

export function RunMascot({ status }: { status: string }) {
	const ok = status === "passed";
	const working = status === "working";
	const gradId = working ? "mgWork" : ok ? "mgPass" : "mgFail";
	const gradStops = working
		? { from: "#a5b4fc", to: "#4f46e5" }
		: ok
			? { from: "#6ee7b7", to: "#059669" }
			: { from: "#fca5a5", to: "#dc2626" };
	return (
		<div className="relative flex flex-col items-center justify-center gap-3 select-none px-4 py-6">
			<style>{`
				@keyframes mascotEnter {
					0%  { transform: scale(0.3) translateY(24px); opacity: 0; }
					65% { transform: scale(1.08) translateY(-4px); opacity: 1; }
					82% { transform: scale(0.96) translateY(2px); }
					100%{ transform: scale(1) translateY(0); }
				}
				@keyframes mascotShake {
					0%,100%{ transform: rotate(0deg) translateX(0); }
					15%    { transform: rotate(-6deg) translateX(-3px); }
					30%    { transform: rotate(6deg)  translateX(3px); }
					50%    { transform: rotate(-4deg) translateX(-2px); }
					70%    { transform: rotate(3deg)  translateX(2px); }
					85%    { transform: rotate(-1deg) translateX(-1px); }
				}
				@keyframes mascotFloat {
					0%,100%{ transform: translateY(0); }
					50%    { transform: translateY(-8px); }
				}
				@keyframes sparkle {
					0%,100%{ opacity:0; transform: scale(0.4) rotate(0deg); }
					40%    { opacity:1; transform: scale(1.1) rotate(160deg); }
				}
				@keyframes teardrop {
					0%  { transform: translateY(0px); opacity:0.9; }
					100%{ transform: translateY(18px); opacity:0; }
				}
				@keyframes labelIn {
					from{ opacity:0; transform:translateY(6px); }
					to  { opacity:1; transform:translateY(0); }
				}
				@keyframes mascotScan {
					0%   { transform: translateY(-32px); opacity: 0; }
					15%  { opacity: 0.85; }
					85%  { opacity: 0.85; }
					100% { transform: translateY(32px); opacity: 0; }
				}
				@keyframes thinkDot {
					0%,70%,100% { opacity: 0.25; transform: translateY(0); }
					35%         { opacity: 1;    transform: translateY(-3px); }
				}
			`}</style>

			{/* Sparkles for pass */}
			{ok && (
				<>
					<span data-tst-decorative-motion className="pointer-events-none absolute left-6 top-4 text-base text-yellow-300" style={{ animation: "sparkle 1.6s ease-in-out 0.1s infinite" }}>✦</span>
					<span data-tst-decorative-motion className="pointer-events-none absolute right-8 top-2 text-sm text-emerald-300" style={{ animation: "sparkle 1.6s ease-in-out 0.5s infinite" }}>✧</span>
					<span data-tst-decorative-motion className="pointer-events-none absolute bottom-10 left-4 text-xs text-yellow-200" style={{ animation: "sparkle 1.6s ease-in-out 0.9s infinite" }}>★</span>
					<span data-tst-decorative-motion className="pointer-events-none absolute bottom-8 right-6 text-sm text-emerald-200" style={{ animation: "sparkle 1.6s ease-in-out 1.3s infinite" }}>✦</span>
				</>
			)}

			{/* SVG face */}
			<div data-tst-decorative-motion style={{ animation: ok || working ? "mascotEnter 0.55s cubic-bezier(0.34,1.56,0.64,1) both, mascotFloat 3s ease-in-out 0.6s infinite" : "mascotShake 0.6s ease-in-out both" }}>
				<svg width="110" height="110" viewBox="0 0 110 110" fill="none" xmlns="http://www.w3.org/2000/svg">
					<defs>
						<radialGradient id={gradId} cx="38%" cy="28%" r="70%">
							<stop offset="0%" stopColor={gradStops.from} />
							<stop offset="100%" stopColor={gradStops.to} />
						</radialGradient>
						<clipPath id="faceClip">
							<circle cx="55" cy="54" r="46" />
						</clipPath>
						<radialGradient id="eyeShine" cx="30%" cy="30%" r="60%">
							<stop offset="0%" stopColor="#fff" stopOpacity="0.95" />
							<stop offset="100%" stopColor="#fff" stopOpacity="0.3" />
						</radialGradient>
					</defs>

					{/* Drop shadow */}
					<ellipse cx="55" cy="103" rx="32" ry="5" fill="rgba(0,0,0,0.18)" />

					{/* Face base */}
					<circle cx="55" cy="54" r="46" fill={`url(#${gradId})`} />
					{/* Highlight rim */}
					<circle cx="55" cy="54" r="46" stroke={working ? "#818cf8" : ok ? "#34d399" : "#f87171"} strokeWidth="2" fill="none" opacity="0.5" />

					{working ? (
						<>
							{/* Animus-style scan sweep, clipped to the face */}
							<g clipPath="url(#faceClip)">
								<rect x="9" y="52" width="92" height="2.5" fill="rgba(224,231,255,0.9)" style={{ animation: "mascotScan 1.9s ease-in-out infinite" }} />
							</g>
							{/* Calm, focused eyes */}
							<circle cx="38" cy="50" r="6" fill="white" opacity="0.95" />
							<circle cx="38" cy="50" r="3" fill="#1a1a2e" />
							<circle cx="72" cy="50" r="6" fill="white" opacity="0.95" />
							<circle cx="72" cy="50" r="3" fill="#1a1a2e" />
							{/* Small concentrating mouth */}
							<path d="M 47 69 Q 55 73 63 69" stroke="white" strokeWidth="3" strokeLinecap="round" fill="none" />
						</>
					) : ok ? (
						<>
							{/* Happy eyes — big glossy circles */}
							<circle cx="38" cy="47" r="9" fill="white" opacity="0.95" />
							<circle cx="38" cy="48" r="5.5" fill="#1a1a2e" />
							<circle cx="40" cy="44.5" r="2" fill="white" />
							<circle cx="43" cy="50" r="0.8" fill="white" opacity="0.6" />

							<circle cx="72" cy="47" r="9" fill="white" opacity="0.95" />
							<circle cx="72" cy="48" r="5.5" fill="#1a1a2e" />
							<circle cx="74" cy="44.5" r="2" fill="white" />
							<circle cx="77" cy="50" r="0.8" fill="white" opacity="0.6" />

							{/* Rosy cheeks */}
							<ellipse cx="20" cy="65" rx="10" ry="7" fill="#f9a8d4" opacity="0.45" />
							<ellipse cx="90" cy="65" rx="10" ry="7" fill="#f9a8d4" opacity="0.45" />

							{/* Big smile */}
							<path d="M 32 68 Q 55 84 78 68" stroke="white" strokeWidth="3.5" strokeLinecap="round" fill="none" />
							{/* Smile highlight */}
							<path d="M 32 68 Q 55 84 78 68" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" strokeLinecap="round" fill="none" strokeDasharray="4 3" />
						</>
					) : (
						<>
							{/* X eyes — thicker for more expression */}
							<line x1="28" y1="38" x2="44" y2="54" stroke="white" strokeWidth="4" strokeLinecap="round" />
							<line x1="44" y1="38" x2="28" y2="54" stroke="white" strokeWidth="4" strokeLinecap="round" />

							<line x1="66" y1="38" x2="82" y2="54" stroke="white" strokeWidth="4" strokeLinecap="round" />
							<line x1="82" y1="38" x2="66" y2="54" stroke="white" strokeWidth="4" strokeLinecap="round" />

							{/* Frown */}
							<path d="M 33 72 Q 55 60 77 72" stroke="white" strokeWidth="3.5" strokeLinecap="round" fill="none" />

							{/* Single teardrop under right X eye */}
							<ellipse cx="74" cy="59" rx="3.5" ry="5" fill="rgba(255,255,255,0.75)" style={{ animation: "teardrop 1.8s ease-in 0.4s infinite" }} />
						</>
					)}
				</svg>
			</div>
			{working && (
				<div className="flex items-center gap-1.5" aria-hidden>
					<span data-tst-decorative-motion className="h-1.5 w-1.5 rounded-full bg-indigo-400" style={{ animation: "thinkDot 1.2s ease-in-out 0s infinite" }} />
					<span data-tst-decorative-motion className="h-1.5 w-1.5 rounded-full bg-indigo-400" style={{ animation: "thinkDot 1.2s ease-in-out 0.2s infinite" }} />
					<span data-tst-decorative-motion className="h-1.5 w-1.5 rounded-full bg-indigo-400" style={{ animation: "thinkDot 1.2s ease-in-out 0.4s infinite" }} />
				</div>
			)}

			{/* Label */}
			<div
				className={`text-sm font-bold tracking-wide ${working ? "text-indigo-500 dark:text-indigo-300" : ok ? "text-success-strong dark:text-emerald-400" : "text-destructive-strong dark:text-red-400"}`}
				style={{ animation: "labelIn 0.4s ease-out 0.35s both" }}
			>
				{working ? "Preparing your test…" : ok ? "All tests passed ✓" : "Tests failed"}
			</div>
			<div
				className="text-[11px] text-muted-foreground"
				style={{ animation: "labelIn 0.4s ease-out 0.5s both" }}
			>
				{working ? "Drafting the plan from your request — follow along in the chat." : ok ? "Great job! Check the chat for the summary." : "Check the chat for error details."}
			</div>
		</div>
	);
}

// ─── Run result card (chat panel) ─────────────────────────────────────────────
// Full error details with copy button live in chat so they stay with the
// conversation context. The right panel just shows the mascot.

export type RunSummaryProps = {
	result: ExecuteResult;
	healing?: boolean;
	onHeal?: () => void;
	onRunAgain?: () => void;
	onApplyInChat?: (text: string) => void;
	onStartNewTest?: (text: string) => void;
	latest?: boolean;
	/** Heal endpoint answered 410 — the affordance is hidden rather than shown broken. */
	healDisabled?: boolean;
	/** Pipeline stage of an in-flight fix proposal (triage_gate … verifying). */
	healStage?: HealStage | null;
};

/**
 * Memoized: one of these renders per completed run in the thread, each with a markdown narrative, a
 * step list and a cached suggestions query. Without this, every store tick re-rendered all of them.
 */
export const RunResultCard = memo(function RunResultCard(props: RunSummaryProps) {
	return <RunSummary {...props} />;
});

/**
 * Structured outcome card for a non-passing run. Org/app issues (audience:user)
 * are shown as actionable guidance — the reason + a "What to do" step the user
 * can act on in their org. Our-code issues (audience:internal) are shown as a
 * muted "we're on it" note with NO org blame (the real detail is in our logs).
 */
function OutcomeCard({ outcome }: { outcome: OutcomeMessage }) {
	const isUser = outcome.audience === "user";
	const causeLabel =
		outcome.cause === "app"
			? "App behaviour"
			: outcome.cause === "org"
				? "Org configuration"
				: "Internal error";
	return (
		<div
			className={`overflow-hidden rounded-lg border ${
				isUser ? "border-primary/40 bg-primary/5" : "border-border bg-muted/30"
			}`}
		>
			<div className="flex items-start gap-2.5 p-3">
				<div
					className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
						isUser ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
					}`}
				>
					{isUser ? <AlertTriangle className="h-4 w-4" /> : <Info className="h-4 w-4" />}
				</div>
				<div className="min-w-0 flex-1 space-y-1.5">
					<div className="flex items-start justify-between gap-2">
						<span
							className={`text-sm font-semibold leading-tight ${isUser ? "text-primary" : "text-foreground"}`}
						>
							{outcome.headline}
						</span>
						<Badge
							variant={isUser ? "default" : "secondary"}
							className="mt-0.5 shrink-0 text-[11px] font-medium uppercase tracking-wide"
						>
							{causeLabel}
						</Badge>
					</div>
					{/* Rich, org-grounded explanation (markdown + mermaid diagrams via Streamdown) when the
					    backend produced one; otherwise the plain-text reason. overflow-x-auto so a wide
					    diagram/code block scrolls inside the card instead of stretching the chat column. */}
					{outcome.explanation ? (
						<div className="max-w-full overflow-x-auto text-xs leading-relaxed">
							<SkillResultMarkdown content={outcome.explanation} isStreaming={false} />
						</div>
					) : (
						<p className="text-xs leading-relaxed text-muted-foreground">{outcome.reason}</p>
					)}
					{/* Always surface the precise, code-authored remediation — even alongside the rich
					    explanation — so the concrete fix is never lost if the LLM softens its steps. */}
					{isUser && outcome.action && (
						<div className="mt-2 flex items-start gap-1.5 rounded-md border border-primary/30 bg-primary/10 px-2.5 py-2 text-xs leading-relaxed">
							<ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
							<span>
								<span className="font-semibold text-primary">What to do: </span>
								<span className="text-foreground/90">{outcome.action}</span>
							</span>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

export function RunSummary({
	result,
	healing,
	onHeal,
	onRunAgain,
	onApplyInChat,
	onStartNewTest,
	latest,
	healDisabled,
	healStage,
}: RunSummaryProps) {
	// `status` is only ever passed|failed, so the run's TRUE outcome rides on `execution_state` —
	// lifted to the top level by `runToExecuteResult` so historical runs carry it too.
	const execState: ExecutionState | undefined =
		result.execution_state ?? result.summary.execution_state;
	// Both of these are PASSES that are not clean. Neither may ever render red.
	const passedWithRetry = execState === "passed_with_retry";
	const degraded = execState === "degraded";
	const ok = result.status === "passed" || passedWithRetry || degraded;
	const cleanPass = ok && !passedWithRetry && !degraded;
	// A BLOCKED run isn't a test failure — the service caught an org/implementation issue (premise
	// that doesn't hold, a capability/permission limit, or a field/action that exists-but-not-on-
	// layout / doesn't exist) and is telling the user what to fix. Render it as actionable guidance,
	// not a red error.
	const blocked = !ok && execState === "blocked";
	const {
		narrative,
		outcome,
		triage,
		needs_human,
		compile_warnings,
		cleanup_warnings,
		flake_budget,
		console_tail,
		records,
		records_narrative,
	} = result.summary;
	const triageMeta = triage ? TRIAGE_META[triage.category] : null;
	// A plan change CAN resolve this failure — the only case where a "Propose fix" / "Apply this fix
	// in chat" affordance makes sense. Org-side failures (blocked, or triage says don't retry: missing
	// setup data, off-layout field, empty org) need a Setup change instead, shown in the outcome text.
	const planFixable = !blocked && triage?.retryRecommended !== false;

	const durationSec = Number.isFinite(result.duration_ms) ? (result.duration_ms / 1000).toFixed(1) : null;

	function buildCopyText() {
		const parts: string[] = [];
		parts.push(`Status: ${result.status}`);
		if (result.run_id) parts.push(`Run: ${result.run_id}`);
		if (durationSec) parts.push(`Duration: ${durationSec}s`);
		if (narrative) parts.push(`\nSummary:\n${narrative}`);
		if (triage) parts.push(`\nTriage: ${triage.category} (${triage.confidence})\n${triage.reasoning}\n→ ${triage.recommendation}`);
		if (result.step_results.length) {
			parts.push(`\nExecuted steps (${result.step_results.length}):`);
			for (const s of result.step_results) {
				const icon = s.status === "passed" ? "✓" : "✗";
				parts.push(`  ${icon} ${s.ordinal}. ${s.plain_english || s.kind}`);
				if (s.error) parts.push(`     Error: ${s.error}`);
			}
		}
		if (console_tail) parts.push(`\nConsole:\n${console_tail}`);
		return parts.join("\n");
	}

	return (
		<div
			className={`space-y-1.5 rounded-md border p-2 text-xs ${
				cleanPass
					? "border-success/40 bg-success/10"
					: ok || blocked
						? "border-warning/40 bg-warning/10"
						: "border-destructive/40 bg-destructive/10"
			}`}
		>
			{/* Header row */}
			<div className="flex flex-wrap items-center gap-2">
				{ok ? (
					<AnimatedCheck />
				) : blocked ? (
					<AlertTriangle className="h-4 w-4 shrink-0 text-warning-strong" />
				) : (
					<AnimatedX />
				)}
				<span className="font-semibold">
					{ok ? "Passed" : blocked ? "Needs attention" : "Failed"}
				</span>
				{execState && <ExecutionStateChip state={execState} />}
				<span className="text-muted-foreground">
					{result.summary.passed}✓{" "}
					{result.summary.failed > 0 && <>{result.summary.failed}✗</>}
					{durationSec && <> in {durationSec}s</>}
				</span>
				{result.summary.token_usage && result.summary.token_usage.total > 0 && (
					<span
						className="inline-flex items-center gap-1 text-[11px] text-muted-foreground/70"
						title={`Run used ~${result.summary.token_usage.total} tokens across ${result.summary.token_usage.calls} model call(s) (triage, narration, fix proposal)`}
					>
						<Zap className="h-3 w-3" />
						{formatTokens(result.summary.token_usage.total)} tokens
					</span>
				)}
				{(() => {
					const fixed = result.step_results.filter(
						(s) => (s.data as Record<string, unknown> | undefined)?.ai_corrected,
					).length;
					if (fixed === 0) return null;
					return (
						<span
							className="inline-flex items-center gap-1 rounded border border-warning/40 bg-warning/10 px-1.5 py-0.5 text-[11px] font-medium text-warning-strong"
							title="The agent auto-corrected steps during this run (e.g. a drifted label)"
						>
							<Sparkles className="h-3 w-3" />
							{fixed} auto-corrected
						</span>
					);
				})()}
				<CopyButton getText={buildCopyText} title="Copy run details" />

				{result.run_id && <RunReportPill runId={result.run_id} />}

				{/* Re-running is a single intent: /execute validates the plan's contract itself, so there
				    is nothing to approve first. */}
				{!healing && !(!ok && triage?.retryRecommended === false) && onRunAgain && (
					<Button
						size="sm"
						variant="outline"
						onClick={onRunAgain}
						className="h-6 px-2 text-[11px]"
						title="Re-run this plan as-is"
					>
						<Play className="mr-1 h-3 w-3" />
						Run again
					</Button>
				)}

				{/* Heal v2 PROPOSES a patch and verifies it against a real run; it never re-runs this
				    plan or changes this run's verdict. Hidden for org-side failures (see `planFixable`):
				    a patch can't fix a missing approval process, an off-layout field, or an empty org. */}
				{onHeal && !healDisabled && planFixable && (
					<Button
						size="sm"
						variant="default"
						onClick={onHeal}
						disabled={healing}
						className="h-6 px-2 text-[11px]"
						title="Diagnose this failure and propose a plan patch for your review — nothing is applied without your approval"
					>
						{healing ? (
							<>
								<Loader2 className="mr-1 h-3 w-3 animate-spin" />
								{healStage ? healStageLabel(healStage) : "Proposing fix…"}
							</>
						) : (
							<>
								<Sparkles className="mr-1 h-3 w-3" />
								Propose fix
							</>
						)}
					</Button>
				)}
				{onHeal && healDisabled && planFixable && (
					<span
						className="text-[11px] italic text-muted-foreground/70"
						title="Automatic fix proposals are switched off for this workspace, so there is nothing to run here."
					>
						healing disabled
					</span>
				)}

				{result.run_id ? (
					<span className="ml-auto font-mono text-[11px] text-muted-foreground">
						run {result.run_id.slice(0, 8)}
					</span>
				) : null}
			</div>

			{/* Prominent error — the actual failure message, surfaced right under the
			    header so the user can act on it instead of digging through the per-step list. */}
			{/* Structured outcome card — supersedes the raw error/narrative when present. Shown for
			    any non-clean verdict, including the amber passes. */}
			{!cleanPass && outcome && <OutcomeCard outcome={outcome} />}

			{/* Why a green run wasn't clean. */}
			{passedWithRetry && flake_budget && <FlakeBudgetCaption budget={flake_budget} />}

			{/* Teardown that didn't complete — leftover test data the user may need to clean up. */}
			{cleanup_warnings && cleanup_warnings.length > 0 && (
				<div className="rounded border border-warning/30 bg-warning/5 p-1.5 text-[11px] text-warning-strong/90">
					<div className="mb-0.5 flex items-center gap-1 font-semibold text-warning-strong">
						<AlertTriangle className="h-3 w-3" />
						Cleanup did not finish
					</div>
					<ul className="ml-4 list-disc space-y-0.5 opacity-90">
						{cleanup_warnings.map((w, i) => (
							<li key={i}>{w}</li>
						))}
					</ul>
				</div>
			)}

			{!ok &&
				!outcome &&
				(() => {
					const errText =
						(result.summary as { error?: string | null }).error ??
						result.summary.first_failure?.error ??
						result.step_results.find((s) => s.status === "failed")?.error ??
						null;
					if (!errText) return null;
					// A blocked verdict ("add the field/action to the layout", "create it", "the org
					// doesn't enforce this rule") is actionable guidance — show it amber, not red.
					return blocked ? (
						<div className="flex items-start gap-1.5 rounded-md border border-primary/30 bg-primary/5 p-2.5 text-xs leading-relaxed text-primary">
							<AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
							<span className="break-words">
								<span className="font-semibold">Action needed: </span>
								{errText}
							</span>
						</div>
					) : (
						<div className="flex items-start gap-1.5 rounded-md border border-destructive/30 bg-destructive/5 p-2.5 text-xs leading-relaxed text-destructive-strong">
							<XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
							<span className="break-words">
								<span className="font-semibold">Error: </span>
								{errText}
							</span>
						</div>
					);
				})()}

			{/* Narrative — rendered through Streamdown (SkillResultMarkdown) so the
			    agent's result reads as markdown, consistent with dev-agent/chat.
			    Suppressed when the structured outcome card is shown (same content). */}
			{narrative && !outcome && (
				<div className="rounded bg-card/60 p-2 text-[11px] leading-snug text-foreground/90">
					<div className="tst-micro mb-1">Agent</div>
					<SkillResultMarkdown content={narrative} isStreaming={false} />
				</div>
			)}

			{/* Triage badge + reasoning */}
			{triageMeta && triage && (
				<div
					className={`flex flex-col gap-1 rounded border p-1.5 ${triageMeta.color}`}
				>
					<div className="flex items-center gap-1.5 font-semibold">
						{triageMeta.icon}
						{triageMeta.label}
						<span className="ml-auto text-[11px] font-normal opacity-70">
							{triage.confidence} confidence
						</span>
					</div>
					<p className="text-[11px] leading-snug opacity-90">
						{triage.reasoning}
					</p>
					<p className="text-[11px] leading-snug opacity-75">
						→ {triage.recommendation}
					</p>
					{/* One-click: prefill chat with the recommended change instead of making
					    the user read the diagnosis and retype it. Hidden when "Propose fix" already
					    covers it, and for org-side failures (see `planFixable`) where the recommendation
					    is a Setup change re-planning can't apply — the text above stays visible as the
					    fix, but there's no chat action to take. */}
					{onApplyInChat && triage.recommendation && !onHeal && planFixable && (
						<button
							type="button"
							onClick={() => onApplyInChat(triage.recommendation)}
							className="mt-1 inline-flex min-h-[24px] w-fit items-center gap-1 rounded border border-current px-1.5 py-0.5 text-[11px] font-medium opacity-90 hover:opacity-100"
						>
							<Sparkles className="h-3 w-3" />
							Apply this fix in chat
						</button>
					)}
				</div>
			)}

			{/* No patch was safe to propose — the failure needs a person. (The healer never applies
			    anything on its own, so there is no "healed after N attempts" story to tell.) */}
			{needs_human && (
				<div className="flex items-center gap-1.5 rounded border border-warning/30 bg-warning/5 p-1.5 text-warning-strong">
					<Wrench className="h-3 w-3 shrink-0" />
					<span>No safe fix could be proposed — human review needed.</span>
				</div>
			)}

			<RunRecordSummary records={records ?? []} narrative={records_narrative ?? null} />

			<ExecutedStepsList steps={result.step_results} defaultOpen={!ok} />

			{/* Auto-adjustments — the agent deterministically tuned the plan to this org
			    (label→API remaps, picklist casing, dropped read-only fields). These are
			    fixes already applied, not warnings to act on, so frame them positively and
			    collapse by default. */}
			{compile_warnings && compile_warnings.length > 0 && (
				<details className="rounded border border-primary/20 bg-primary/5 text-[11px] text-foreground/80">
					<summary className="flex cursor-pointer select-none items-center gap-1 p-1.5 font-semibold text-primary">
						<Sparkles className="h-3 w-3" />
						{compile_warnings.length} auto-adjustment
						{compile_warnings.length > 1 ? "s" : ""} to match your org
					</summary>
					<ul className="ml-5 list-disc space-y-0.5 px-2 pb-2 opacity-90">
						{compile_warnings.map((w, i) => (
							<li key={i}>{w}</li>
						))}
					</ul>
				</details>
			)}

			{/* Console tail — collapsed by default */}
			{console_tail && (
				<details className="rounded bg-card/60">
					<summary className="flex cursor-pointer select-none items-center gap-2 p-1.5">
						<span className="font-semibold text-muted-foreground hover:text-foreground">
							Console output
						</span>
						<span className="ml-auto" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
							<CopyButton getText={() => console_tail as string} title="Copy console output" />
						</span>
					</summary>
					<pre className="min-h-[80px] max-h-64 overflow-x-hidden overflow-y-auto p-2 font-mono text-[11px] leading-snug text-foreground/75 whitespace-pre-wrap [overflow-wrap:anywhere]">
						{console_tail}
					</pre>
				</details>
			)}

			{/* What to test next — LLM suggestions grounded in what THIS run touched. Click one to seed
			    chat and have the agent build it as a NEW test (onStartNewTest → no activePlanId), not an
			    edit of this plan. */}
			{latest && result.run_id && onStartNewTest && (
				<RunSuggestions runId={result.run_id} onApplyInChat={onStartNewTest} />
			)}
		</div>
	);
}

/**
 * "What to test next" chips for a finished run. Cached per run id via react-query (see
 * useRunSuggestions), so re-renders of the chat tree don't re-issue a request per visible card.
 * Renders nothing when the org can't be read or there's nothing to suggest — the block degrades to
 * silence, never an error. Clicking a chip seeds chat with the agent instruction.
 */
export function RunSuggestions({
	runId,
	onApplyInChat,
}: {
	runId: string;
	onApplyInChat: (text: string) => void;
}) {
	const { data: suggestions } = useRunSuggestions(runId);

	if (!suggestions || suggestions.length === 0) return null;

	return (
		<div className="rounded border border-primary/20 bg-primary/5 p-2">
			<div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-primary">
				<Sparkles className="h-3 w-3" />
				What to test next
			</div>
			<div className="flex flex-wrap gap-1.5">
				{suggestions.map((s) => (
					<button
						key={s.title}
						type="button"
						onClick={() => onApplyInChat(s.chat_prompt)}
						title={s.chat_prompt}
						className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-card/60 px-2 py-1 text-[11px] font-medium text-foreground/90 transition-colors hover:bg-primary/10 hover:text-primary"
					>
						<ArrowRight className="h-3 w-3 text-primary" />
						{s.title}
					</button>
				))}
			</div>
		</div>
	);
}

export function RunRecordSummary({
	records,
	narrative,
}: {
	records: Array<Record<string, unknown>>;
	narrative: string | null;
}) {
	if (!records.length && !narrative) return null;
	const grouped = records.reduce<Record<string, string[]>>((acc, record) => {
		const sobject = String(record.sobject ?? "Record");
		const id = String(record.id ?? "");
		if (!id) return acc;
		acc[sobject] = [...(acc[sobject] ?? []), id];
		return acc;
	}, {});
	return (
		<div className="rounded border border-primary/30 bg-primary/5 p-2 text-[11px]">
			<div className="mb-1 font-semibold text-primary">Records used in this run</div>
			{narrative && <p className="mb-1 text-foreground/90">{narrative}</p>}
			{Object.entries(grouped).map(([sobject, ids]) => (
				<div key={sobject} className="flex flex-wrap gap-1.5 py-0.5">
					<span className="font-semibold text-foreground/80">{sobject}</span>
					{ids.map((id) => (
						<code
							key={id}
							className="rounded bg-card/80 px-1 py-0.5 font-mono text-[11px] text-muted-foreground"
						>
							{id}
						</code>
					))}
				</div>
			))}
		</div>
	);
}

export function ExecutedStepsList({
	steps,
	defaultOpen,
}: {
	steps: ExecuteResult["step_results"];
	defaultOpen?: boolean;
}) {
	if (!steps.length) return null;

	return (
		<details className="rounded bg-card/60" open={defaultOpen}>
			<summary className="cursor-pointer select-none p-1.5 font-semibold text-muted-foreground hover:text-foreground">
				Executed steps ({steps.length})
			</summary>
			<div className="max-h-72 overflow-y-auto border-t border-border/50">
				{steps.map((step) => (
					<div
						key={`${step.ordinal}-${step.plain_english ?? step.kind}`}
						className="border-b border-border/30 px-2 py-1.5 last:border-0"
					>
						<div className="flex items-start gap-2">
							<span className="mt-0.5 font-mono text-[11px] text-muted-foreground">
								{step.ordinal}
							</span>
							{step.status === "passed" ? (
								<CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-success-strong" />
							) : (
								<XCircle className="mt-0.5 h-3 w-3 shrink-0 text-destructive-strong" />
							)}
							<div className="min-w-0 flex-1">
								<div className="flex flex-wrap items-center gap-1.5">
									<span
										className={
											step.status === "passed"
												? "text-foreground/90"
												: "font-semibold text-destructive-strong"
										}
									>
										{step.plain_english || step.kind}
									</span>
									<span className="rounded bg-muted/50 px-1 py-0.5 font-mono text-[11px] text-muted-foreground">
										{step.kind}
									</span>
									{Boolean((step.data as Record<string, unknown>)?.ai_corrected) &&
										(step.status === "passed" ? (
											// "AI fixed" only when the correction actually made the step pass —
											// claiming "fixed" on a step that still failed would be a lie.
											<span className="rounded border border-success/30 bg-success/10 px-1.5 py-0.5 text-[11px] font-medium text-success-strong">
												AI fixed
											</span>
										) : (
											<span className="rounded border border-warning/30 bg-warning/10 px-1.5 py-0.5 text-[11px] font-medium text-warning-strong">
												AI tried fix
											</span>
										))}
									{typeof step.duration_ms === "number" && (
										<span className="ml-auto font-mono text-[11px] text-muted-foreground">
											{step.duration_ms}ms
										</span>
									)}
								</div>
								{/* AI correction diff — show original → corrected when the AI changed the step */}
								{Boolean((step.data as Record<string, unknown>)?.ai_corrected) &&
									Boolean((step.data as Record<string, unknown>)?.corrected_step_description) && (
									<div className="mt-1 flex items-center gap-1 rounded border border-success/20 bg-success/5 px-1.5 py-1 text-[11px]">
										<span className="shrink-0 text-muted-foreground/60 line-through">
											{step.plain_english || step.kind}
										</span>
										<span className="shrink-0 text-muted-foreground">→</span>
										<span className="text-success-strong">
											{String((step.data as Record<string, unknown>).corrected_step_description)}
										</span>
									</div>
								)}
								{step.error && (
									<pre className="mt-1 max-h-28 overflow-auto rounded border border-destructive/30 bg-destructive/5 p-1.5 font-mono text-[11px] leading-snug text-destructive-strong/90 whitespace-pre-wrap [overflow-wrap:anywhere]">
										{step.error}
									</pre>
								)}
							</div>
						</div>
					</div>
				))}
			</div>
		</details>
	);
}
